import { DateTime as DT } from 'luxon';
import type { TaskCardType } from '@econtractor/types';
import {
    isoDateToEndMillis,
    isoDateToStartMillis,
    millisToIsoDate,
    millisToTime,
    resolveDateEndMillis,
} from '../../NewTask/helpers/initNewTaskStruct';
import { USERS_HISTORY_MARKER } from './taskUsersHelpers';

/** Службовий маркер у reject_comment для подій зміни терміну */
export const TERM_HISTORY_MARKER = '__TERM__';
export const REMIND_HISTORY_MARKER = '__REMIND__';

export interface TaskDueDateValues {
    multiDay: boolean;
    withTime: boolean;
    dateStart: number;
    dateEnd: number;
    endTime: string;
}

export const dueDatesFromTask = (task: TaskCardType): TaskDueDateValues => {
    const withTime = Boolean(task.withTime);
    const endTimeRaw = String(task.endTime || '').trim();
    const endTime = endTimeRaw
        ? endTimeRaw.slice(0, 5)
        : millisToTime(task.dateEnd);

    return {
        multiDay: Boolean(task.multiDay),
        withTime,
        dateStart: Number(task.dateStart) || DT.now().setZone('local').startOf('day').toMillis(),
        dateEnd: Number(task.dateEnd) || DT.now().setZone('local').endOf('day').toMillis(),
        endTime: endTime || '18:00',
    };
};

export const normalizeDueDateValues = (values: TaskDueDateValues) => {
    const dateEnd = resolveDateEndMillis(values.dateEnd, values.withTime, values.endTime);
    const dateStart = values.multiDay
        ? DT.fromMillis(values.dateStart, { zone: 'local' }).startOf('day').toMillis()
        : DT.fromMillis(dateEnd, { zone: 'local' }).startOf('day').toMillis();

    return {
        multiDay: values.multiDay,
        withTime: values.withTime,
        dateStart,
        dateEnd,
        endTime: values.withTime ? String(values.endTime || '18:00').trim() || null : null,
    };
};

/** Як у таблиці: start без часу, end з часом якщо withTime */
const formatTermPoint = (millis: number, withTime: boolean): string => {
    const dt = DT.fromMillis(millis, { zone: 'local' });
    if (!dt.isValid) return '—';
    return withTime ? dt.toFormat('dd.MM.yyyy HH:mm') : dt.toFormat('dd.MM.yyyy');
};

/** Коментар історії: `15.07.2026 → 20.07.2026 18:00` (в один рядок) */
export const buildTermHistoryComment = (
    values: ReturnType<typeof normalizeDueDateValues>,
): string => {
    const startLabel = formatTermPoint(values.dateStart, false);
    const endLabel = formatTermPoint(values.dateEnd, values.withTime);
    return `${TERM_HISTORY_MARKER}\n${startLabel} → ${endLabel}`;
};

export const parseHistoryComment = (
    raw: string | null | undefined,
): { kind: 'term' | 'users' | 'remind' | 'plain'; text: string } => {
    const value = String(raw || '');
    const termPrefix = `${TERM_HISTORY_MARKER}\n`;
    if (value.startsWith(termPrefix)) {
        return { kind: 'term', text: value.slice(termPrefix.length) };
    }
    if (value.startsWith(TERM_HISTORY_MARKER)) {
        return { kind: 'term', text: value.slice(TERM_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    const usersPrefix = `${USERS_HISTORY_MARKER}\n`;
    if (value.startsWith(usersPrefix)) {
        return { kind: 'users', text: value.slice(usersPrefix.length) };
    }
    if (value.startsWith(USERS_HISTORY_MARKER)) {
        return { kind: 'users', text: value.slice(USERS_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    const remindPrefix = `${REMIND_HISTORY_MARKER}\n`;
    if (value.startsWith(remindPrefix)) {
        return { kind: 'remind', text: value.slice(remindPrefix.length) };
    }
    if (value.startsWith(REMIND_HISTORY_MARKER)) {
        return { kind: 'remind', text: value.slice(REMIND_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    return { kind: 'plain', text: value };
};

export const applyEndDateChange = (
    values: TaskDueDateValues,
    isoDate: string,
): Partial<TaskDueDateValues> => {
    const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
    if (endMillis == null) return {};
    if (!values.multiDay) {
        return {
            dateEnd: endMillis,
            dateStart: isoDateToStartMillis(isoDate) ?? values.dateStart,
        };
    }
    return { dateEnd: endMillis };
};

export const applyStartDateChange = (
    values: TaskDueDateValues,
    isoDate: string,
): Partial<TaskDueDateValues> => {
    const startMillis = isoDateToStartMillis(isoDate);
    if (startMillis == null) return {};

    const next: Partial<TaskDueDateValues> = { dateStart: startMillis };
    const endStartOfDay = isoDateToStartMillis(millisToIsoDate(values.dateEnd));
    if (endStartOfDay != null && startMillis > endStartOfDay) {
        const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
        if (endMillis != null) next.dateEnd = endMillis;
    }
    return next;
};

export const applyWithTimeChange = (
    values: TaskDueDateValues,
    checked: boolean,
): Partial<TaskDueDateValues> => {
    const endIso = millisToIsoDate(values.dateEnd);
    const endMillis = isoDateToEndMillis(endIso, checked, values.endTime);
    return {
        withTime: checked,
        ...(endMillis != null ? { dateEnd: endMillis } : {}),
    };
};

export const applyEndTimeChange = (
    values: TaskDueDateValues,
    time: string,
): Partial<TaskDueDateValues> => {
    const endIso = millisToIsoDate(values.dateEnd);
    const endMillis = isoDateToEndMillis(endIso, true, time);
    return {
        endTime: time,
        ...(endMillis != null ? { dateEnd: endMillis } : {}),
    };
};

export const applyMultiDayChange = (
    values: TaskDueDateValues,
    checked: boolean,
): Partial<TaskDueDateValues> => {
    if (!checked) {
        return {
            multiDay: false,
            dateStart: isoDateToStartMillis(millisToIsoDate(values.dateEnd)) ?? values.dateStart,
        };
    }
    return { multiDay: true };
};
